import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { Product, UtmParamsContext } from '@app/core/models';
import { GTMService, productBookButton, ProductBookButtonActionGtm } from '@app/core/services/tracking';
import { areProductsForWeightLoss, DetectMobile, Helper, ProductType } from '@app/shared/utils';
import { RetailChannel, Tool, ViewType } from '@app/shared/utils/enums';
import { IconName } from '@app/shared/utils/icon/icons';
import { DialogHelpers } from '@app/shared/utils/static-helpers/dialog-helpers';
import { ProductsFacade } from '@app/store/products/products.facade';
import { VetFacade } from '@app/store/vet';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PurchaseProductsPopinComponent } from '../purchase-products-popin/purchase-products-popin.component';
import { ShareRecoEvent } from '@app/core/services/tracking/types/share-reco-event';
import { ShareRecommendationFacade } from '@app/store/share-recommendation';
import { isNotNullOrUndefined } from '@app/shared/operators/not-null-or-undefined';
import { LocaleContentEnum } from '@app/shared/data';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass, AsyncPipe, SlicePipe } from '@angular/common';
import { IconComponent } from '../../utils/icon/icon.component';
import { RCFieldModule } from '@rc/ui';
import { TranslatePipe, TranslationCodePipe, PublicUrlPipe, SafeHtmlPipe } from '@app/shared/pipes';
import { AllowanceFacade } from '@app/store/allowance';
import { CoreFacade } from '@app/store/core/core.facade';

@Component({
  selector: 'app-product-footer',
  templateUrl: './product-footer.component.html',
  styleUrls: ['product-footer.component.scss'],
  imports: [
    NgClass,
    IconComponent,
    FormsModule,
    ReactiveFormsModule,
    RCFieldModule,
    AsyncPipe,
    SlicePipe,
    TranslatePipe,
    TranslationCodePipe,
    PublicUrlPipe,
    SafeHtmlPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductFooterComponent implements OnInit, OnDestroy {
  @Input() withMobileSearch = false;
  @Input() multiSelectionAvailable = false;
  @Input() disableMultiSelection = false;
  @Input() selectedProducts: Product[] = [];
  @Input() displayPurchaseButtonInput = false;
  @Input() showContinueOnPrimaryButton = true;

  @Output() removeProduct: EventEmitter<Product> = new EventEmitter<Product>();
  @Output() openMoreProductsPopin: EventEmitter<Product[]> = new EventEmitter<Product[]>();
  @Output() primaryContinueAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() secondaryContinueAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() searchAction: EventEmitter<void> = new EventEmitter<void>();

  public readonly LocaleContentEnum = LocaleContentEnum;
  public readonly ProductType = ProductType;
  public readonly IconName = IconName;
  public readonly viewType = ViewType;

  public toggleProductsSelection = false;
  public productsByMarginCount: number = 0;
  public isPricesPage$ = this.route.params.pipe(map((params: Params) => params['state'] === 'prices'));
  public currentViewType$ = this.isPricesPage$.pipe(map((isPricesPage) => (isPricesPage ? ViewType.Edit : ViewType.Grid)));
  public showEditPricesActions$ = this.productsFacade.showPriceListActions$;
  public priceListUpdateLoading$ = this.productsFacade.priceListUpdateLoading$;
  public isPriceUploadEnabled$ = this.vetFacade.isPriceUploadEnabled$;
  public allProductsTypeAvailable = false;
  public isMobile = DetectMobile.isMobileNotDeprecated();

  public readonly isRapidReco$ = this.coreFacade.toolInfo$.pipe(
    first(),
    map(({ tool }) => tool === Tool.RapidReco),
  );
  public isRRV2Enabled$ = this.vetFacade.isFeatureEnabled$('VS_RRV2');

  public displayPurchaseButton$ = combineLatest([this.vetFacade.conversionConfiguration$, this.productsFacade.selectedProducts$]).pipe(
    map(([conversionConfiguration, products]) => {
      const hasOnlyMufProducts = !products.find((selectedProduct) => selectedProduct.retailChannel !== RetailChannel.MULTIFUNCTION);
      const conversionState = conversionConfiguration !== null && conversionConfiguration.showPurchaseButton !== false;
      return this.displayPurchaseButtonInput && conversionState && !hasOnlyMufProducts;
    }),
  );

  public showWLPTooltip$: Observable<boolean> = this.vetFacade.isFeatureEnabled$('VS_RRV3').pipe(
    filter((isRRV3Enabled) => isRRV3Enabled),
    map((_) => areProductsForWeightLoss(this.selectedProducts) && !this.isContinueDisabled()),
  );

  public form: UntypedFormGroup;

  private showTreatsPlaceholder = false;
  private isTreatsEnabled$ = this.vetFacade.isTreatsEnabled$;
  private _destroyed$ = new Subject<void>();

  constructor(
    private readonly modal: MatDialog,
    private readonly vetFacade: VetFacade,
    private readonly productsFacade: ProductsFacade,
    private readonly shareRecoFacade: ShareRecommendationFacade,
    private readonly route: ActivatedRoute,
    private readonly trackingService: GTMService,
    private readonly allowanceFacade: AllowanceFacade,
    private readonly coreFacade: CoreFacade,
    private readonly formBuilder: UntypedFormBuilder,
  ) {}

  public get marginControl(): AbstractControl<number | null> {
    return this.form?.get('margin') as FormControl;
  }

  ngOnInit(): void {
    combineLatest([this.productsFacade.hasDryProduct$, this.productsFacade.hasWetProduct$])
      .pipe(
        takeUntil(this._destroyed$),
        tap(([hasDryProduct, hasWetProduct]) => {
          if (hasDryProduct && hasWetProduct) {
            this.allProductsTypeAvailable = true;
          }
        }),
      )
      .subscribe();

    this.isTreatsEnabled$.subscribe((value) => {
      this.showTreatsPlaceholder = value;
    });

    this.setupForm();
    this.handleMarginFieldState();
  }

  showProductPlaceholder(productType: ProductType): boolean {
    const showProductPlaceholder =
      !this.multiSelectionAvailable && !this.selectedProducts.some((product) => product.productType === productType);
    if (productType === ProductType.Treats) {
      return showProductPlaceholder && this.showTreatsPlaceholder;
    }
    return showProductPlaceholder;
  }

  toggleProductSelection(): void {
    this.toggleProductsSelection = !this.toggleProductsSelection;
  }

  cancelEditPrices(): void {
    this.productsFacade.resetProductsPricesState();
  }

  submitEditPrices(): void {
    this.productsFacade.updatePrices();
  }

  openProductsPurchase(): void {
    this.vetFacade.conversionConfiguration$
      .pipe(
        first(),
        tap((conversionConfiguration) => {
          this.trackingService.sendInteraction(
            productBookButton(ProductBookButtonActionGtm.PURCHASE_PRODUCT, conversionConfiguration?.trackingCode),
          );
        }),
      )
      .subscribe();

    combineLatest([
      this.vetFacade.clinic$,
      this.vetFacade.vet$.pipe(map(({ id: vetId, user: { countryCode } }) => ({ countryCode, vetId }))),
      this.productsFacade.selectedProducts$.pipe(
        map((products) => products.map(({ id, speciesCode }) => ({ id, speciesCode }))),
        switchMap((productsIdsAndSpeciesCodes) =>
          this.shareRecoFacade.selectRecommendationId$(productsIdsAndSpeciesCodes.map(({ id }) => id)).pipe(
            filter(isNotNullOrUndefined),
            map((recoId) => ({ productsIdsAndSpeciesCodes, recoId })),
          ),
        ),
      ),
      this.allowanceFacade.allowanceParams$,
    ])
      .pipe(
        take(1),
        switchMap(([{ id: clinicId, companyAddress }, { vetId, countryCode }, { productsIdsAndSpeciesCodes, recoId }, allowanceParams]) => {
          const shareRecoEvent: ShareRecoEvent = {
            clinicId,
            countryCode,
            eventTime: new Date(),
            eventType: 'productCatalog',
            productIds: productsIdsAndSpeciesCodes.map(({ id }) => id),
            sharedRecommendationId: recoId,
            vetId,
            speciesCode: productsIdsAndSpeciesCodes[0].speciesCode,
            tool: allowanceParams.tool,
          };
          return this.trackingService.sendShareRecoEvent(clinicId, shareRecoEvent, companyAddress?.country);
        }),
      )
      .subscribe();

    const dialogConfig = {
      ...DialogHelpers.defaultConfig('productPurchase'),
      data: { context: UtmParamsContext.QRCODEPURCHASEPOPIN_PRODUCTBOOK },
    };
    this.modal.open(PurchaseProductsPopinComponent, dialogConfig).afterClosed().subscribe();
  }

  isContinueDisabled(): boolean {
    return this.selectedProducts.length > 0 && this.selectedProducts.every((product) => product.productType === ProductType.Treats);
  }

  closeTooltip(ref: HTMLElement): void {
    ref.style.display = 'none';
  }

  onKeyDown(event: KeyboardEvent): void {
    Helper.preventTypingNonNumericChars(event);
  }
  onPaste(event: ClipboardEvent): void {
    Helper.preventPastingNonNumericChars(event);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private setupForm(): void {
    this.form = this.formBuilder.group({
      margin: [{ value: null, disabled: true }],
    });

    this.marginControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._destroyed$))
      .subscribe((margin: number) => {
        this.productsFacade.setPriceMargin(margin);
      });
  }

  private handleMarginFieldState(): void {
    this.productsFacade.priceListToUpdate$
      .pipe(
        takeUntil(this._destroyed$),
        tap((prices) => {
          const uniqueProductCount = new Set(prices.filter((price) => price.updateByMargin).map((price) => price.productId)).size;
          uniqueProductCount > 0 ? this.marginControl.enable() : this.marginControl.disable();
          this.productsByMarginCount = uniqueProductCount;
          if (!prices.some((price) => price.updateByMargin)) {
            this.marginControl.setValue(null);
          }
        }),
      )
      .subscribe();
  }
}
